import { useState, useEffect } from "react";
import { ref, onValue, off } from "firebase/database";
import {
  collection,
  onSnapshot,
  setDoc,
  doc,
  getDoc,
  deleteDoc
} from "firebase/firestore";
import { db, rtdb } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

function isKidProfile(profile) {
  return profile.accountType === "Kid";
}

export function useFriendManager(user) {
  const [kidsOnline, setKidsOnline] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsOnline, setFriendsOnline] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);     // 收到的邀請
  const [pendingRequests, setPendingRequests] = useState([]);   // 自己發出的邀請

  // 1. 監聽 RTDB kidsOnline
  useEffect(() => {
    if (!user || !user.uid) return;
    const statusRef = ref(rtdb, "status");
    const cb = async (snap) => {
      const statusData = snap.val() || {};
      const uids = Object.entries(statusData)
        .filter(([uid, s]) => s.state === "online")
        .map(([uid]) => uid);

      const arr = await Promise.all(
        uids.map(async uid => {
          const docSnap = await getDoc(doc(db, "users", uid));
          if (!docSnap.exists()) return null;
          const data = docSnap.data();
          if (!isKidProfile(data)) return null;
          return { uid, ...data };
        })
      );
      setKidsOnline(arr.filter(Boolean));
    };
    const errorCb = (error) => {
      console.error("🔥 RTDB onValue error:", error);
    };
    onValue(statusRef, cb, errorCb);
    return () => off(statusRef, "value", cb);
  }, [user?.uid]);

  // 2. Firestore 監聽自己的好友
  useEffect(() => {
    if (!user) return;
    const colRef = collection(db, "users", user.uid, "friends");
    const unsub = onSnapshot(colRef, (snap) => {
      const arr = [];
      snap.forEach(doc => arr.push({ uid: doc.id, ...doc.data() }));
      setFriends(arr);
    });
    return () => unsub();
  }, [user]);

  // 3. 監聽收到的好友邀請（對方邀請你）
  useEffect(() => {
    if (!user) return;
    const reqCol = collection(db, "users", user.uid, "friendRequests");
    const unsub = onSnapshot(reqCol, (snap) => {
      const arr = [];
      snap.forEach(doc => arr.push({ uid: doc.id, ...doc.data() }));
      setFriendRequests(arr);
    });
    return () => unsub();
  }, [user]);

  // 4. 監聽自己送出的好友邀請（你邀請對方，等待對方同意）
  useEffect(() => {
    if (!user) return;
    // 你自己送出的 pendingRequests：去所有 online kids 檢查你是否在他們的 friendRequests 裡
    // 這裡為了 demo，我們只查詢目前 online 的 kids
    Promise.all(kidsOnline.map(async kid => {
      if (kid.uid === user.uid) return null;
      const docSnap = await getDoc(doc(db, "users", kid.uid, "friendRequests", user.uid));
      if (docSnap.exists()) {
        return { uid: kid.uid, ...kid, status: 'pending' };
      }
      return null;
    })).then(arr => setPendingRequests(arr.filter(Boolean)));
  }, [user, kidsOnline]);

  // 5. friendsOnline（只看線上的好友）
  useEffect(() => {
    const myFriendUids = new Set(friends.map(f => f.uid));
    setFriendsOnline(
      kidsOnline.filter(k => myFriendUids.has(k.uid))
    );
  }, [kidsOnline, friends]);

  // 發送好友邀請（送出邀請 request 到對方 friendRequests 子集合）
  const sendFriendRequest = async (targetUser) => {
    if (!user || !targetUser?.uid) return;
    if (targetUser.uid === user.uid) return alert("You can't add yourself!");
    try {
      await setDoc(
        doc(db, "users", targetUser.uid, "friendRequests", user.uid),
        {
          fromUid: user.uid,
          fromName: user.firstName || "",
          avatar: user.avatar || "",
          sentAt: Date.now()
        }
      );
      alert("Friend request sent!");
    } catch (e) {
      alert("Failed to send friend request: " + e.message);
    }
  };

  // 接受好友邀請
const acceptFriendRequest = async (request) => {
  if (!user || !request?.uid) return;
  try {
    const functions = getFunctions(); // region 可加也可不加
    const acceptFn = httpsCallable(functions, "acceptFriendRequest");
    const result = await acceptFn({ otherUid: request.uid });  // 注意 key 必須叫 otherUid！
    console.log("Cloud function result:", result);
    alert("Friend added!");
  } catch (e) {
    alert("Failed to accept friend request: " + (e.message || e));
    console.error("acceptFriendRequest error", e);
  }
};

  // 拒絕好友邀請
  const denyFriendRequest = async (request) => {
    if (!user || !request?.uid) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "friendRequests", request.uid));
    } catch (e) {
      alert("Failed to deny friend request: " + e.message);
    }
  };

  // 刪除好友
  const removeFriend = async (friend) => {
    if (!user || !friend?.uid) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "friends", friend.uid));
      // 可選：單向刪除或雙向刪除
    } catch (e) {
      alert("Failed to remove friend: " + e.message);
    }
  };

  const messageFriend = (friend) => {};

  return {
    kidsOnline,
    friends,
    friendsOnline,
    friendRequests,
    pendingRequests,
    sendFriendRequest,
    acceptFriendRequest,
    denyFriendRequest,
    removeFriend,
    messageFriend
  };
}
